import { createFileRoute, notFound, useNavigate, useRouter } from "@tanstack/react-router";
import { SPLIT_TYPE, getSplitTypeLabel, type ExpenseSplitView } from "@/lib/types";
import { loadExpenseDetail, deleteExpenseFn, updateExpenseFn } from "@/lib/server-fns/expenses";
import {
  Container,
  Heading,
  Card,
  Text,
  VStack,
  HStack,
  Button,
  Badge,
  Separator,
  Table,
  Flex,
  Box,
  Input,
  NativeSelect,
  NumberInput,
  Field,
  Grid,
  SegmentGroup,
  For,
  Collapsible,
} from "@chakra-ui/react";
import { LuArrowLeft, LuTrash2, LuPencil, LuChevronDown } from "react-icons/lu";
import { useState } from "react";
import { formatDate, formatAmount } from "@/lib/format";
import { UserNameCell } from "@/components/user-name-cell";
import { getUser, getDisplayName, buildParticipantOptions } from "@/lib/user-utils";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { showErrorToast } from "@/lib/toast-utils";
import { SplitParticipants } from "@/components/split-participants";
import { InputGroup } from "@/components/ui/input-group";
import { CURRENCY_ICONS, SUPPORTED_CURRENCIES } from "@/lib/constants";
import { NotFoundContent, ErrorContent } from "@/components/error-page";

export const Route = createFileRoute("/_authenticated/expenses/$id")({
  loader: async ({ params }) => {
    try {
      return await loadExpenseDetail({ data: { id: params.id } });
    } catch (error) {
      if (error instanceof Error && error.message === "Expense not found") {
        throw notFound();
      }
      throw error;
    }
  },
  notFoundComponent: () => (
    <NotFoundContent
      title="Expense not found"
      description="This expense doesn't exist or you don't have access to it."
      backHref="/expenses"
      backLabel="Back to Activity"
    />
  ),
  errorComponent: ({ error, reset }) => <ErrorContent error={error} reset={reset} />,
  component: ExpenseDetail,
});

function ExpenseDetail() {
  const { expense, friends, currentUser } = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const ctx = { currentUser, friends };
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Edit form state — initialized from the loaded expense
  const [editDescription, setEditDescription] = useState(expense.description);
  const [editDate, setEditDate] = useState(expense.date);
  const [editAmount, setEditAmount] = useState(expense.amount);
  const [editCurrency, setEditCurrency] = useState(expense.currency);
  const [editPaidBy, setEditPaidBy] = useState(expense.paidBy);
  const [editSplitType, setEditSplitType] = useState(expense.splitType);
  const [editSplits, setEditSplits] = useState<ExpenseSplitView[]>(expense.splits);

  const participantOptions = buildParticipantOptions({ currentUser, friends });

  const formattedAmount = formatAmount(expense.amount, expense.currency, { absolute: false });

  const handleDelete = async () => {
    try {
      await deleteExpenseFn({ data: { id: expense.id } });
      navigate({ to: "/expenses" });
    } catch (error) {
      showErrorToast("Failed to delete expense", error);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const isoDate = new Date(editDate).toISOString().split("T")[0];

      await updateExpenseFn({
        data: {
          id: expense.id,
          description: formData.get("description") as string,
          amount: formData.get("amount") as string,
          currency: editCurrency,
          paidBy: editPaidBy,
          splitType: editSplitType,
          splits: editSplits,
          date: isoDate,
        },
      });

      await router.invalidate();
      setIsEditing(false);
    } catch (error) {
      showErrorToast("Failed to update expense", error);
    }
  };

  const handleCancelEdit = () => {
    // Reset edit state back to current expense values
    setEditDescription(expense.description);
    setEditDate(expense.date);
    setEditAmount(expense.amount);
    setEditCurrency(expense.currency);
    setEditPaidBy(expense.paidBy);
    setEditSplitType(expense.splitType);
    setEditSplits(expense.splits);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Container maxW={{ base: "md", md: "3xl" }} px={{ base: "0", md: "4" }}>
        <HStack mb="6" px={{ base: "4", md: "0" }}>
          <Button variant="ghost" onClick={handleCancelEdit}>
            <LuArrowLeft size={16} />
            Back to Expense
          </Button>
        </HStack>

        <form onSubmit={handleEditSubmit}>
          <input type="hidden" name="splitType" value={editSplitType} />
          <input type="hidden" name="currency" value={editCurrency} />
          <input type="hidden" name="paidBy" value={editPaidBy} />

          {/* Collapsible settings */}
          <Collapsible.Root mb="4">
            <Collapsible.Trigger asChild>
              <Button variant="outline" size="lg" w="full" justifyContent="space-between">
                <Text fontSize="sm">
                  {editCurrency} · {getDisplayName(editPaidBy, { currentUser, friends })} paid ·{" "}
                  {getSplitTypeLabel(editSplitType)}
                </Text>
                <Collapsible.Indicator
                  transition="transform 0.2s"
                  _open={{ transform: "rotate(180deg)" }}
                >
                  <LuChevronDown />
                </Collapsible.Indicator>
              </Button>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Card.Root variant="outline" mt="2">
                <Card.Body p={{ base: "4", md: "6" }}>
                  <VStack gap="4" align="stretch">
                    <Field.Root required>
                      <Field.Label>Currency</Field.Label>
                      <SegmentGroup.Root
                        value={editCurrency}
                        onValueChange={(details) =>
                          setEditCurrency(details.value ?? expense.currency)
                        }
                        width="full"
                        css={{
                          "--segment-indicator-bg": "colors.loonie.solid",
                          "--segment-indicator-shadow": "shadows.md",
                        }}
                      >
                        <SegmentGroup.Indicator />
                        <For each={SUPPORTED_CURRENCIES}>
                          {(code) => (
                            <SegmentGroup.Item key={code} value={code}>
                              <SegmentGroup.ItemText>
                                <HStack gap="1.5">
                                  {CURRENCY_ICONS[code]}
                                  {code}
                                </HStack>
                              </SegmentGroup.ItemText>
                              <SegmentGroup.ItemHiddenInput />
                            </SegmentGroup.Item>
                          )}
                        </For>
                      </SegmentGroup.Root>
                    </Field.Root>

                    <Field.Root required>
                      <Field.Label>Paid By</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={editPaidBy}
                          onChange={(e) => setEditPaidBy(e.target.value)}
                        >
                          {participantOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>Split Type</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={editSplitType}
                          onChange={(e) => setEditSplitType(e.target.value)}
                        >
                          <option value={SPLIT_TYPE.EQUAL}>Split Equally</option>
                          <option value={SPLIT_TYPE.EXACT}>Exact Amounts</option>
                          <option value={SPLIT_TYPE.PERCENTAGE}>By Percentage</option>
                          <option value={SPLIT_TYPE.SHARES}>By Shares</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Collapsible.Content>
          </Collapsible.Root>

          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="4" alignItems="start">
            {/* Left: Expense details */}
            <Card.Root variant="outline">
              <Card.Body p={{ base: "4", md: "6" }}>
                <Heading as="h2" size="lg" mb="6">
                  Edit Expense
                </Heading>

                <VStack gap="4" align="stretch">
                  <Field.Root required>
                    <Field.Label>Description</Field.Label>
                    <Input
                      name="description"
                      placeholder="Dinner, Groceries, Rent, etc."
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                    />
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label>Date</Field.Label>
                    <Input
                      type="date"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                    />
                  </Field.Root>

                  <Field.Root required>
                    <Field.Label>Total Amount</Field.Label>
                    <Field.HelperText>Enter the total expense amount</Field.HelperText>
                    <NumberInput.Root
                      name="amount"
                      min={0.01}
                      step={0.01}
                      value={editAmount}
                      onValueChange={(details) => setEditAmount(details.value)}
                    >
                      <NumberInput.Control />
                      <InputGroup startElement={CURRENCY_ICONS[editCurrency]}>
                        <NumberInput.Input placeholder="25.00" />
                      </InputGroup>
                    </NumberInput.Root>
                  </Field.Root>
                </VStack>
              </Card.Body>
            </Card.Root>

            {/* Right: Split participants */}
            <Card.Root variant="outline">
              <Card.Body p={{ base: "4", md: "6" }}>
                <Text fontSize="sm" fontWeight="medium" mb="4">
                  Split With
                </Text>
                <SplitParticipants
                  currentUser={currentUser}
                  friends={friends}
                  splits={editSplits}
                  splitType={editSplitType}
                  onSplitsChange={setEditSplits}
                  totalAmount={editAmount}
                  currency={editCurrency}
                />
              </Card.Body>
            </Card.Root>
          </Grid>

          <Flex
            direction={{ base: "column-reverse", md: "row" }}
            justify="flex-end"
            gap="3"
            mt="4"
            px={{ base: "4", md: "0" }}
          >
            <Button
              variant="ghost"
              w={{ base: "full", md: "auto" }}
              onClick={handleCancelEdit}
              type="button"
            >
              Cancel
            </Button>
            <Button type="submit" w={{ base: "full", md: "auto" }}>
              Save Changes
            </Button>
          </Flex>
        </form>
      </Container>
    );
  }

  return (
    <Container maxW="4xl" px={{ base: "0", md: "4" }}>
      <HStack mb="6" px={{ base: "4", md: "0" }} justify="space-between">
        <Button variant="ghost" onClick={() => router.history.back()}>
          <LuArrowLeft size={16} />
          Back to Activity
        </Button>
        <HStack gap="2">
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
            <LuPencil size={16} />
            Edit
          </Button>
          <Button variant="ghost" colorPalette="red" size="sm" onClick={() => setDeleteOpen(true)}>
            <LuTrash2 size={16} />
            Delete
          </Button>
        </HStack>
      </HStack>

      <Card.Root variant="outline">
        <Card.Body p={{ base: "4", md: "8" }}>
          <VStack gap="4" align="stretch">
            <Flex
              direction={{ base: "column-reverse", md: "row" }}
              justify="space-between"
              align={{ base: "stretch", md: "flex-start" }}
              gap={{ base: "2", md: "4" }}
            >
              <Box>
                <Heading as="h2" size="lg" mb="1">
                  {expense.description}
                </Heading>
                <Text fontSize="sm" color="fg.muted">
                  Paid by{" "}
                  {getDisplayName(expense.paidBy, ctx, { youLabel: currentUser.name || "You" })}
                </Text>
              </Box>
              <Heading as="h1" size="2xl" color="loonie.fg" flexShrink={0}>
                {formattedAmount}
              </Heading>
            </Flex>

            <Separator />

            <Flex gap={{ base: "4", md: "8" }} wrap="wrap">
              <VStack gap="1" align="start">
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  Date
                </Text>
                <Text fontSize="lg">{formatDate(expense.date)}</Text>
              </VStack>

              <VStack gap="1" align="start">
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  Currency
                </Text>
                <Text fontSize="lg">{expense.currency}</Text>
              </VStack>

              <VStack gap="1" align="start">
                <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                  Split Type
                </Text>
                <Badge size="lg" variant="subtle">
                  {getSplitTypeLabel(expense.splitType)}
                </Badge>
              </VStack>
            </Flex>

            {expense.splits.length > 0 && (
              <>
                <Separator />

                <Box>
                  <Text fontSize="lg" fontWeight="medium" mb="4">
                    Split Details
                  </Text>

                  {/* Desktop table view */}
                  <Box display={{ base: "none", md: "block" }}>
                    <Table.Root>
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Participant</Table.ColumnHeader>
                          <Table.ColumnHeader>Amount</Table.ColumnHeader>
                          {expense.splitType === SPLIT_TYPE.PERCENTAGE && (
                            <Table.ColumnHeader>Percentage</Table.ColumnHeader>
                          )}
                          {expense.splitType === SPLIT_TYPE.SHARES && (
                            <Table.ColumnHeader>Shares</Table.ColumnHeader>
                          )}
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {expense.splits.map((split, index) => {
                          const user = getUser(split.userId, ctx);
                          const identifier = user.email || split.userId;

                          return (
                            <Table.Row key={index}>
                              <Table.Cell>
                                <UserNameCell
                                  name={user.name}
                                  identifier={identifier}
                                  displayName={getDisplayName(split.userId, ctx)}
                                  truncate
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Text fontWeight="medium">
                                  {formatAmount(split.amount, expense.currency, {
                                    absolute: false,
                                  })}
                                </Text>
                              </Table.Cell>
                              {expense.splitType === SPLIT_TYPE.PERCENTAGE && (
                                <Table.Cell>{split.percentage}%</Table.Cell>
                              )}
                              {expense.splitType === SPLIT_TYPE.SHARES && (
                                <Table.Cell>{split.shares}</Table.Cell>
                              )}
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>
                  </Box>

                  {/* Mobile list view */}
                  <VStack
                    display={{ base: "flex", md: "none" }}
                    gap="0"
                    align="stretch"
                    borderWidth="1px"
                    borderColor="border"
                    borderRadius="md"
                  >
                    {expense.splits.map((split, index) => {
                      const user = getUser(split.userId, ctx);
                      const identifier = user.email || split.userId;

                      return (
                        <Flex
                          key={index}
                          justify="space-between"
                          align="center"
                          px="3"
                          py="3"
                          borderBottomWidth={index < expense.splits.length - 1 ? "1px" : "0"}
                          borderColor="border"
                          gap="3"
                        >
                          <Box flex="1" minW="0">
                            <UserNameCell
                              name={user.name}
                              identifier={identifier}
                              displayName={getDisplayName(split.userId, ctx)}
                              truncate
                            />
                          </Box>
                          <HStack gap="3" flexShrink={0}>
                            <Text fontWeight="medium">
                              {formatAmount(split.amount, expense.currency, { absolute: false })}
                            </Text>
                            {expense.splitType === SPLIT_TYPE.PERCENTAGE && (
                              <Text fontSize="sm" color="fg.muted">
                                {split.percentage}%
                              </Text>
                            )}
                            {expense.splitType === SPLIT_TYPE.SHARES && (
                              <Text fontSize="sm" color="fg.muted">
                                {split.shares} shares
                              </Text>
                            )}
                          </HStack>
                        </Flex>
                      );
                    })}
                  </VStack>
                </Box>
              </>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete Expense"
        description={`Are you sure you want to delete "${expense.description}"? This will reverse all associated ledger entries and cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </Container>
  );
}
